import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from "@fluentui/react-components";
import { MoreHorizontal24Regular } from "@fluentui/react-icons";

const useOverflowMenuStyles = makeStyles({
  menu: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  menuButton: {
    alignSelf: "center",
  },
});

type OverflowMenuItemProps = {
  tab: string;
  onClick: React.MouseEventHandler<any>;
  icon: any;
};

/**
 * A menu item for an overflow menu that only displays when the tab is not visible
 */
const OverflowMenuItem = (props: OverflowMenuItemProps) => {
  const { tab, onClick } = props;
  const isVisible = useIsOverflowItemVisible(tab);

  if (isVisible) {
    return null;
  }

  return (
    <MenuItem key={tab} onClick={onClick} icon={props.icon}>
      {tab}
    </MenuItem>
  );
};

type OverflowItem = {
  key: string;
  text: string;
  icon: any;
};

type OverflowMenuProps = {
  items: OverflowItem[];
  onTabSelect?: (tabId: string) => void;
};

/**
 * A menu for selecting tabs that have overflowed and are not visible.
 */
const OverflowMenu = (props: OverflowMenuProps) => {
  const { onTabSelect } = props;
  const { ref, isOverflowing, overflowCount } =
    useOverflowMenu<HTMLButtonElement>();

  const styles = useOverflowMenuStyles();

  const onItemClick = (tabId: string) => {
    onTabSelect?.(tabId);
  };

  if (!isOverflowing) {
    return null;
  }

  return (
    <Menu hasIcons>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          className={styles.menuButton}
          ref={ref}
          icon={<MoreHorizontal24Regular />}
          aria-label={`${overflowCount} more tabs`}
          role="tab"
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList className={styles.menu}>
          {props.items.map((item) => (
            <OverflowMenuItem
              key={item.key}
              tab={item.text}
              onClick={() => onItemClick(item.key)}
              icon={item.icon}
            />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default OverflowMenu;
